import * as fromApi from "../../../core/models/loginResponse";
import { Policy } from "../../../core/models/policy";
import { v4 as uuidv4 } from "uuid";

const padLeft = (value: number): string => {
  return `${value < 10 ? "0" : ""}${value}`;
};

export const formatDate = (date: Date): string => {
  return `${padLeft(date.getMonth() + 1)}/${padLeft(
    date.getDate()
  )}/${date.getFullYear()}`;
};

export const formatDateFromISOString = (value: string): string => {
  return formatDate(new Date(value));
};

export const formatDateFromYYYYMMDDString = (value: string): string => {
  try {
    if (value && Number.isInteger(+value)) {
      return formatDate(
        new Date(+value.slice(0, 4), +value.slice(4, 6) - 1, +value.slice(6, 8))
      );
    }
    return value;
  } catch (error) {
    return value;
  }
};

export const createPolicies = (response: fromApi.LoginResponse): Policy[] => {
  const futurePolicies: Policy[] = [];
  const presentPolicies: Policy[] = [];

  response?.accounts?.forEach((account) => {
    account.policies?.forEach((policy) => {
      const p = createPolicy(policy);
      p.accountNumber = account.accountNumber;
      presentPolicies.push(p);
    });

    account.futures?.forEach((policy) => {
      const p = createPolicy(policy);
      p.accountNumber = account.accountNumber;
      p.futurePolicy = true;
      futurePolicies.push(p);
    });
  });

  return [...presentPolicies, ...futurePolicies];
};

export const createPolicy = (p: fromApi.Policy): Policy => {
  const result: Policy = {
    accountNumber: "",
    agencyId: p.agencyId,
    allowCardPrint: p.allowCardPrint,
    cancelled: p.cancelled,
    cancelledDate: formatDateFromYYYYMMDDString(p.cancelledDate),
    drivers: p.drivers?.map((d) => {
      return {
        first: d.first,
        full: d.full,
        last: d.last,
        rated: d.rated,
      };
    }),
    effectiveDate: p.effectiveDate,
    expirationDate: p.expirationDate,
    filterCancelled: p.filterCancelled,
    futurePolicy: false,
    insured: {
      address: p.insured.address,
      city: p.insured.city,
      phone: p.insured.phone,
      primary: {
        full: p.insured.name1,
        first: p.insured.name1F,
        last: p.insured.name1L,
      },
      secondary: {
        full: p.insured.name2,
        first: "",
        last: "",
      },
      state: p.insured.state,
      zip: p.insured.zip,
    },
    liabilityLimit: p.liabilityLimit,
    locations: p.locations?.map((location) => {
      return {
        address1: location.address1,
        city: location.city,
        state: location.state,
        zip: location.zip,
      };
    }),
    packageNumber: p.packageNumber,
    paperlessPolicy: {
      ...p.paperlessPolicy,
      policy: p.policyNumber,
      packageNumber: p.packageNumber,
    },
    policyNumber: p.policyNumber,
    policyPremium: p.policyPremium,
    riskState: p.riskState,
    typeDescription: p.typeDescription,
    units: p.units?.map((unit) => {
      return {
        address: unit.address,
        city: unit.city,
        constructionYear: unit.constructionYear,
        coverages: [
          { description: "Description 1", limit: "$1" },
          { description: "Description 2", limit: "$2" },
        ],
        heatingType: unit.heatingType,
        livingAreaTotal: unit.livingAreaTotal,
        numberOfStories: unit.numberOfStories,
        protectionClass: unit.protectionClass,
        roofAge: unit.roofAge,
        roofType: unit.roofType,
        state: unit.state,
        swimmingPool: unit.swimmingPool,
        unitNumber: unit.unitNumber,
        woodstove: unit.woodstove,
        zip: unit.zip,
      };
    }),
    vehicles: p.vehicles?.map((v) => {
      return {
        address: v.address,
        city: v.city,
        id: uuidv4(),
        make: v.make,
        makeModel: v.makeModel,
        model: v.model,
        unitNumber: v.unitNumber,
        state: v.state,
        vin: v.vin,
        year: v.year,
        zip: v.zip,
      };
    }),
  };
  return result;
};
